import { api } from "../../api"
import {
  CalendarEventProjectResponse,
  CalendarEventResponse,
  CalendarEventTaskResponse,
} from "./calendar-event-response"
import { CalendarEventViewModel, makeCalendarEventViewModel } from "./calendar-event"
import { makeCalendarEventApiEndpoints } from "./endpoints"
import { TreeGridColors } from "../../treegrid/types"
import { OptionType } from "../../tasks/api/task-response"
import { getQueryArgsByEndpoint, hasPendingMutations } from "../../utils/rtk-query-util"
import { applyTimeRangeChange, TimeRange } from "../utils/calendar-event-time-range"

const apiEndpoints = makeCalendarEventApiEndpoints()
const pendingEndpointNames = [
  "updateCalendarEventCustomers",
  "updateCalendarEventParticipants",
  "updateCalendarEventSuppliers",
  "updateCalendarEventWorkspaces",
  "updateCalendarEventProject",
  "updateCalendarEventTask",
]
export const calendarEventApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCalendarEvents: builder.query<CalendarEventViewModel[], EventsQueryParams | void>({
      query: (params) => ({
        method: "GET",
        url: apiEndpoints.events(),
        params: params ? new URLSearchParams(params) : undefined,
      }),
      transformResponse: (response: { calendarEvents: CalendarEventResponse[] }) => {
        return response.calendarEvents.map(makeCalendarEventViewModel)
      },
      providesTags: (result = []) => [
        "CalendarEvent",
        ...result.map(({ id }) => ({ type: "CalendarEvent", id } as const)),
      ],
    }),
    createCalendarEvent: builder.mutation<CalendarEventViewModel, NewCalendarEventData>({
      query: (eventData) => ({
        method: "POST",
        url: apiEndpoints.events(),
        body: eventData,
      }),
      transformResponse: makeCalendarEventViewModel,
      invalidatesTags: ["CalendarEvent"],
    }),
    updateCalendarEventInfo: builder.mutation<
      CalendarEventViewModel,
      { eventId: string } & CalendarEventInfoUpdateData
    >({
      query: ({ eventId, ...updateData }) => ({
        method: "PUT",
        url: apiEndpoints.eventItem(eventId),
        body: updateData,
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, ...updateData }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        // Apply time range logic if startDate/endDate are part of the update
        let optimisticUpdate = { ...updateData }
        if (updateData.startDate || updateData.endDate) {
          const event = queryData.entry?.data?.find((e: any) => e.id === eventId)
          if (event) {
            const currentRange: TimeRange = {
              startDate: event.startDate,
              endDate: event.endDate,
            }
            optimisticUpdate = applyTimeRangeChange(currentRange, updateData)
          }
        }
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              Object.assign(event, optimisticUpdate)
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: "updateCalendarEventInfo" }))
            dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", { type: "CalendarEvent", id: eventId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateCalendarEventProject: builder.mutation<
      CalendarEventViewModel,
      { eventId: string; project: CalendarEventProjectResponse | null }
    >({
      query: ({ eventId, project }) => ({
        method: "PATCH",
        url: apiEndpoints.projectRelation(eventId),
        body: { project: project ? project.id : null },
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, project }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              const savedProjectId = event.project?.id
              event.project = project
              if (savedProjectId !== project?.id) {
                event.task = null // remove task relation if project is changed since tasks are project specific
              }
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", { type: "CalendarEvent", id: eventId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateCalendarEventTask: builder.mutation<
      CalendarEventViewModel,
      { eventId: string; task: CalendarEventTaskResponse | null }
    >({
      query: ({ eventId, task }) => ({
        method: "PATCH",
        url: apiEndpoints.taskRelation(eventId),
        body: { task: task ? task.id : null },
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, task }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              event.task = task
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", { type: "CalendarEvent", id: eventId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateCalendarEventCustomers: builder.mutation<
      CalendarEventViewModel,
      { eventId: string; customers: OptionType[] }
    >({
      query: ({ eventId, customers }) => ({
        method: "PATCH",
        url: apiEndpoints.customerRelations(eventId),
        body: { customers: customers.map((c) => c.id) },
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, customers }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              event.customers = customers
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", { type: "CalendarEvent", id: eventId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateCalendarEventParticipants: builder.mutation<
      CalendarEventViewModel,
      { eventId: string; participants: OptionType[] }
    >({
      query: ({ eventId, participants }) => ({
        method: "PATCH",
        url: apiEndpoints.participantRelations(eventId),
        body: {
          participants: participants.map((p) => p.id),
        },
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, participants }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              event.participants = participants
            }
          })
        )
        try {
          await queryFulfilled
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateCalendarEventSuppliers: builder.mutation<
      CalendarEventViewModel,
      { eventId: string; suppliers: OptionType[] }
    >({
      query: ({ eventId, suppliers }) => ({
        method: "PATCH",
        url: apiEndpoints.supplierRelations(eventId),
        body: { suppliers: suppliers.map((supplier) => supplier.id) },
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, suppliers }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              event.suppliers = suppliers
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", { type: "CalendarEvent", id: eventId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    updateCalendarEventWorkspaces: builder.mutation<
      CalendarEventViewModel,
      { eventId: string; workspaces: OptionType[] }
    >({
      query: ({ eventId, workspaces }) => ({
        method: "PATCH",
        url: apiEndpoints.workspaceRelations(eventId),
        body: { workspaces: workspaces.map((workspace) => workspace.id) },
      }),
      transformResponse: makeCalendarEventViewModel,
      async onQueryStarted({ eventId, workspaces }, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData
        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const event = draft.find((e) => e.id === eventId)
            if (event) {
              event.workspaces = workspaces
            }
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", { type: "CalendarEvent", id: eventId }]))
        } catch {
          // rollback all optimistic updates
          patchResult.undo()
        }
      },
    }),
    deleteEvent: builder.mutation<void, string>({
      query: (eventId) => ({
        method: "DELETE",
        url: apiEndpoints.eventItem(eventId),
      }),
      async onQueryStarted(eventId, { dispatch, queryFulfilled, getState }) {
        const queryData = getQueryArgsByEndpoint(getState, "getCalendarEvents")
        if (!queryData) return
        const { args } = queryData

        const patchResult = dispatch(
          calendarEventApi.util.updateQueryData("getCalendarEvents", args, (draft) => {
            const index = draft.findIndex((e) => e.id === eventId)
            if (index !== -1) draft.splice(index, 1)
          })
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: ["CalendarEvent"],
    }),
  }),
})

export const {
  useGetCalendarEventsQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventInfoMutation,
  useUpdateCalendarEventProjectMutation,
  useUpdateCalendarEventTaskMutation,
  useUpdateCalendarEventCustomersMutation,
  useUpdateCalendarEventParticipantsMutation,
  useUpdateCalendarEventSuppliersMutation,
  useUpdateCalendarEventWorkspacesMutation,
  useDeleteEventMutation,
} = calendarEventApi

type BaseEventsQueryParams = { upcoming?: "true" }
type OrgEventsQueryParams = BaseEventsQueryParams & { organisation: string }
type CustomerEventsQueryParams = BaseEventsQueryParams & { customer: string }
type MemberEventsQueryParams = BaseEventsQueryParams & { participant: string }
type ProjectEventsQueryParams = BaseEventsQueryParams & { project: string }
type SupplierEventsQueryParams = BaseEventsQueryParams & { supplier: string }
type TaskEventsQueryParams = BaseEventsQueryParams & { task: string }
type WorkspaceEventsQueryParams = BaseEventsQueryParams & { workspace: string }

export type EventsQueryParams =
  | BaseEventsQueryParams
  | OrgEventsQueryParams
  | CustomerEventsQueryParams
  | MemberEventsQueryParams
  | ProjectEventsQueryParams
  | SupplierEventsQueryParams
  | TaskEventsQueryParams
  | WorkspaceEventsQueryParams

export type NewCalendarEventData = {
  title: string
  startDate: string
  endDate: string
  description?: string | null
  project?: string | null
  task?: string | null
  organisation?: string | null
  participants?: string[]
  customers?: string[]
  suppliers?: string[]
  workspaces?: string[]
  recurranceType?: string | null
  recurranceEndDate?: string | null
  eventLength?: number // should only be used to send the event length timestamp to the server for recurring instances
  parentEventId?: string | null
}

export type CalendarEventInfoUpdateData = {
  title?: string
  description?: string | null
  startDate?: string
  endDate?: string
  color?: TreeGridColors | null
  recurranceType?: string | null
  recurranceEndDate?: string | null
}
